import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'

interface UseMapBboxProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  bbox?: mapboxgl.LngLatBoundsLike
  maxBbox?: mapboxgl.LngLatBoundsLike
  isPublic?: boolean
  padding: number | mapboxgl.PaddingOptions
  autoFitBounds: boolean
  isDragPan: boolean
  bboxTransitionDuration: number
  isFlyingToRef: React.MutableRefObject<boolean>
  hasInitiallyFit: React.MutableRefObject<boolean>
}

/**
 * Hook to handle bbox fitting and maxBounds management
 */
export const useMapBbox = ({
  map,
  mapLoaded,
  bbox,
  maxBbox,
  isPublic = false,
  padding,
  autoFitBounds,
  isDragPan,
  bboxTransitionDuration,
  isFlyingToRef,
  hasInitiallyFit,
}: UseMapBboxProps) => {
  // Handle bbox changes and initial fit
  useEffect(() => {
    if (!map.current || !mapLoaded || !bbox) {
      return
    }

    // Don't fit to bounds if a flyTo animation is in progress
    if (isFlyingToRef.current) {
      return
    }

    // Only fit to bounds once on initial load if autoFitBounds is true
    if (!hasInitiallyFit.current && (autoFitBounds || isDragPan)) {
      map.current.fitBounds(bbox, {
        padding,
        duration: bboxTransitionDuration,
      })
      hasInitiallyFit.current = true
      // Note: dragPan is enabled by default and configured with inertia settings in Map.tsx
      // No need to call dragPan.enable() here as it would reset the inertia configuration
    }
  }, [
    bbox,
    isDragPan,
    mapLoaded,
    padding,
    autoFitBounds,
    bboxTransitionDuration,
  ])

  // Handle maxBounds
  useEffect(() => {
    if (!map.current || !mapLoaded || !maxBbox) return

    if (isDragPan || isPublic) {
      map.current.setMaxBounds(maxBbox)
      // Note: dragPan is enabled by default and configured with inertia settings in Map.tsx
      // No need to call dragPan.enable() here as it would reset the inertia configuration
    }
  }, [map, maxBbox, isDragPan, isPublic, mapLoaded])
}
